import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ApiErrorsWidgetComponent } from 'src/app/admin/dashboard/dashboard-widgets/api-errors-widget/api-errors-widget.component';
import { Company, User } from 'src/app/classes/models';
import { WidgetInfoGroup } from 'src/app/interfaces/widget-info-group';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyService } from 'src/app/services/company.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

import { WidgetInfo } from '../../../interfaces/widget-info';
import { AccountingAlertsWidgetComponent } from '../dashboard-widgets/accounting-alerts-widget/accounting-alerts-widget.component';
import { AlertsMetricsWidgetComponent } from '../dashboard-widgets/alerts-metrics-widget/alerts-metrics-widget.component';
import { ContractsWidgetComponent } from '../dashboard-widgets/contracts-widget/contracts-widget.component';
import { MyVendorsWidgetComponent } from '../dashboard-widgets/my-vendors-widget/my-vendors-widget.component';
import { QuickSourceWidgetComponent } from '../dashboard-widgets/quick-source-widget/quick-source-widget.component';
import { SpotlightVendorsWidgetComponent } from '../dashboard-widgets/spotlight-vendors-widget/spotlight-vendors-widget.component';
import { VendorRatingsWidgetComponent } from '../dashboard-widgets/vendor-ratings-widget/vendor-ratings-widget.component';
import { VendorStatusesWidgetComponent } from '../dashboard-widgets/vendor-statuses-widget/vendor-statuses-widget.component';
import { VendorTradesWidgetComponent } from '../dashboard-widgets/vendor-trades-widget/vendor-trades-widget.component';
import { ViveDirectoryWidgetComponent } from '../dashboard-widgets/vive-directory-widget/vive-directory-widget.component';

@Component({
  selector: 'app-client-dashboard',
  templateUrl: './client-dashboard.component.html',
  styleUrls: ['./client-dashboard.component.less'],
  providers: [SharedDataService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ClientDashboardComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private AuthService: AuthService,
    private ToastService: ToastService,
    private SharedDataService: SharedDataService,
    private CompanyService: CompanyService,
    private UtilityService: UtilityService
  ) {
    this.CompanyId = this.AuthService.getUserCompanyId();
    this.LoggedInUser = this.AuthService.getUser();

    this.SharedDataServiceSubScription =
      this.SharedDataService.listenForEvent().subscribe({
        next: ({ eventName, data }) => {
          if (eventName === 'GetLoggedInUser') {
            if (!this.LoggedInUser) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing user information.',
                { returnError: true }
              );
            }

            this.SharedDataService.sendEvent(
              'SetLoggedInUser',
              new User(this.LoggedInUser)
            );
            this._cdr.markForCheck();
          }
        }
      });
  }

  widgetInfoGroup: WidgetInfoGroup = { widgetInfoCollection: [] };
  LoggedInUser?: User;
  CompanyId!: number;
  Company?: Company;
  SharedDataServiceSubScription: Subscription;

  ngOnDestroy(): void {
    this.SharedDataServiceSubScription.unsubscribe();
  }

  ngOnInit(): void {
    if (!this.LoggedInUser) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing user information.',
        { returnError: true }
      );
    }

    const { UserAccountRole, UserAccountType: { name } = {} } =
      this.LoggedInUser;

    if (!UserAccountRole) {
      throw this.ToastService.addErrorToast(
        'Error',
        'User is missing permissions.',
        { returnError: true }
      );
    }

    const { role } = UserAccountRole;

    if (
      !role ||
      !['CLIENT_ADMIN', 'CLIENT_VENDOR_AND_SITES_MANAGER'].includes(role)
    ) {
      throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
        returnError: true
      });
    }

    // Load company data to get hasApConfig and other settings
    this.CompanyService.getCompany(
      this.CompanyId,
      this.UtilityService.stringifyObjectsInQueryObject({
        aliasedAttributes: ['hasApConfig']
      })
    ).subscribe({
      next: (Company: Company) => {
        this.Company = Company;

        if (!this.Company) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing company info.',
            { returnError: true }
          );
        }

        const {
          hasApConfig,
          verifyClientRegisteredVendorBusinessOnly,
          allowAutomatedBusinessVerificationClientRegisteredVendors
        } = this.Company;

        if (
          role === 'CLIENT_ADMIN' &&
          !verifyClientRegisteredVendorBusinessOnly
        ) {
          const AlertAccountingContractsWidgets = [
            {
              componentType: AlertsMetricsWidgetComponent,
              show: true,
              class: 'col-12 pb-4'
            } as WidgetInfo
          ];

          if (hasApConfig) {
            AlertAccountingContractsWidgets.push({
              componentType: AccountingAlertsWidgetComponent,
              show: true,
              class: 'col-12 pb-4'
            } as WidgetInfo);
          }

          AlertAccountingContractsWidgets.push({
            componentType: ContractsWidgetComponent,
            show: true,
            class: 'col-12 pb-4'
          } as WidgetInfo);

          Object.assign(this.widgetInfoGroup, {
            class: 'row g-3',
            widgetInfoCollection: [
              {
                class: 'col-12 col-sm-8',
                show: true,
                widgetInfoCollection: [
                  {
                    class: 'row flex-column',
                    show: true,
                    widgetInfoCollection: [
                      {
                        componentType: VendorStatusesWidgetComponent,
                        show: true,
                        class: 'col-12 pb-4'
                      } as WidgetInfo
                    ]
                  } as WidgetInfoGroup,
                  {
                    class: 'row',
                    show: true,
                    widgetInfoCollection: [
                      {
                        class: 'col-6 pb-4',
                        show: true,
                        widgetInfoCollection: [
                          {
                            componentType: VendorTradesWidgetComponent,
                            show: true,
                            class: 'col-12 pb-4'
                          } as WidgetInfo,
                          {
                            componentType: VendorRatingsWidgetComponent,
                            show: true,
                            class: 'col-12 pb-4'
                          } as WidgetInfo
                        ]
                      },
                      {
                        class: 'col-6 pb-4',
                        show: true,
                        widgetInfoCollection: [
                          {
                            componentType: QuickSourceWidgetComponent,
                            show: true,
                            class: 'col-12 pb-4'
                          } as WidgetInfo,
                          {
                            componentType: SpotlightVendorsWidgetComponent,
                            show: true,
                            class: 'col-12 pb-4'
                          } as WidgetInfo
                        ]
                      }
                    ]
                  }
                ]
              } as WidgetInfoGroup,
              {
                class: 'col-12 col-sm-4',
                show: true,
                widgetInfoCollection: [
                  {
                    class: 'row flex-column',
                    show: true,
                    widgetInfoCollection: AlertAccountingContractsWidgets
                  } as WidgetInfoGroup
                ]
              } as WidgetInfoGroup
            ]
          });
        }

        if (
          role === 'CLIENT_VENDOR_AND_SITES_MANAGER' &&
          !verifyClientRegisteredVendorBusinessOnly
        ) {
          Object.assign(this.widgetInfoGroup, {
            class: 'row g-3',
            widgetInfoCollection: [
              {
                class: 'col-12 col-sm-4',
                show: true,
                widgetInfoCollection: [
                  {
                    class: 'row flex-column h-100',
                    show: true,
                    widgetInfoCollection: [
                      {
                        componentType: AlertsMetricsWidgetComponent,
                        show: true,
                        class: 'col-12 pb-4'
                      } as WidgetInfo,
                      {
                        componentType: QuickSourceWidgetComponent,
                        show: true,
                        class: 'col-12 pb-4'
                      }
                    ]
                  } as WidgetInfoGroup
                ]
              } as WidgetInfoGroup,
              {
                class: 'col-12 col-sm-4',
                show: true,
                widgetInfoCollection: [
                  {
                    class: 'row flex-column',
                    show: true,
                    widgetInfoCollection: [
                      {
                        componentType: MyVendorsWidgetComponent,
                        show: true,
                        class: 'col-12 pb-4'
                      } as WidgetInfo,
                      {
                        componentType: VendorRatingsWidgetComponent,
                        show: true,
                        class: 'col-12 pb-4'
                      } as WidgetInfo
                    ]
                  } as WidgetInfoGroup
                ]
              } as WidgetInfoGroup,
              {
                class: 'col-12 col-sm-4 d-flex flex-column',
                show: true,
                widgetInfoCollection: [
                  {
                    class: 'row flex-column',
                    show: true,
                    widgetInfoCollection: [
                      {
                        componentType: ViveDirectoryWidgetComponent,
                        show: true,
                        class: 'col-12 pb-4'
                      },
                      {
                        componentType: ContractsWidgetComponent,
                        show: true,
                        class: 'col-12 pb-4'
                      }
                    ]
                  } as WidgetInfoGroup
                ]
              } as WidgetInfoGroup
            ]
          });
        }

        if (
          name === 'Client' &&
          verifyClientRegisteredVendorBusinessOnly &&
          allowAutomatedBusinessVerificationClientRegisteredVendors
        ) {
          Object.assign(this.widgetInfoGroup, {
            class: 'row g-3',
            widgetInfoCollection: [
              {
                class: 'col-12 col-sm-8',
                show: true,
                widgetInfoCollection: [
                  {
                    class: 'row flex-column',
                    show: true,
                    widgetInfoCollection: [
                      {
                        componentType: VendorStatusesWidgetComponent,
                        show: true,
                        class: 'col-12 pb-4'
                      } as WidgetInfo
                    ]
                  } as WidgetInfoGroup
                ]
              } as WidgetInfoGroup,
              {
                class: 'col-12 col-sm-4',
                show: new Set(['CLIENT_ADMIN']).has(role),
                widgetInfoCollection: [
                  {
                    class: 'row flex-column',
                    show: true,
                    widgetInfoCollection: [
                      {
                        componentType: AccountingAlertsWidgetComponent,
                        show: true,
                        class: 'col-12 pb-4'
                      } as WidgetInfo,
                      {
                        componentType: ApiErrorsWidgetComponent,
                        show: true,
                        class: 'col-12 pb-4'
                      } as WidgetInfo
                    ]
                  } as WidgetInfoGroup
                ]
              } as WidgetInfoGroup
            ]
          });
        }

        this._cdr.markForCheck();
      }
    });
  }
}
